import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Download, BookOpen, FileText, ShieldCheck, Scale, Users,
  Heart, Lock, Eye, Award, AlertTriangle, ChevronRight, ExternalLink,
} from "lucide-react";
import { api, API as API_BASE } from "../lib/api";

const SECTION_ICONS = {
  constitution:   FileText,
  "public-benefit": Heart,
  mission:        BookOpen,
  code:           ShieldCheck,
  coi:            Scale,
  safeguarding:   ShieldCheck,
  edi:            Users,
  finance:        Award,
  risk:           AlertTriangle,
  privacy:        Lock,
  declaration:    Eye,
};

/**
 * /governance — The "Smart Governance Book" of ABGN-UK.
 *
 * A single navigable hub presenting every governance document the Charity
 * Commission expects to see in a CIO application: constitution, public
 * benefit, mission, code of conduct, conflict of interest, safeguarding,
 * EDI, financial controls, risk register, privacy notice, trustee
 * declaration template — plus a one-click PDF download of the full pack.
 *
 * Designed to be inspectable in 30 seconds by a Charity Commission
 * case-worker, a funder, or a prospective trustee.
 */
export default function Governance() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/governance/index").then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const pdfUrl = `${API_BASE}/governance/pack.pdf`;

  return (
    <div className="bg-pearl min-h-[calc(100vh-5rem)]" data-testid="governance-page">
      {/* HERO */}
      <section className="relative bg-navy text-pearl py-16">
        <div className="absolute inset-0 opacity-15 pointer-events-none"
          style={{ backgroundImage: "radial-gradient(circle at 85% 20%, rgba(207,160,82,0.55) 0%, transparent 55%)" }} />
        <div className="max-w-5xl mx-auto px-4 sm:px-6 relative">
          <Link to="/" className="inline-flex items-center gap-2 text-xs text-pearl/65 hover:text-pearl mb-5">
            ← Back to home
          </Link>
          <p className="text-[10px] uppercase tracking-[0.3em] text-saffron">Governance · CIO application pack</p>
          <h1 className="font-display text-4xl sm:text-6xl text-pearl mt-2 leading-tight">The ABGN-UK Governance Book</h1>
          <p className="mt-4 max-w-2xl text-pearl/80 font-serif text-lg leading-relaxed">
            Every document a Charity Commission case-worker, funder, or
            prospective trustee might want to see — in one place, kept
            current, downloadable as a single signed PDF.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <a
              href={pdfUrl}
              data-testid="governance-download-pack"
              className="inline-flex items-center gap-2 px-5 py-3 bg-saffron text-navy rounded-md text-sm font-medium hover:bg-saffron-light"
            >
              <Download size={16} /> Download full Governance Pack (PDF)
            </a>
            <a
              href="#sections"
              className="inline-flex items-center gap-2 px-5 py-3 bg-white/10 text-pearl border border-pearl/25 rounded-md text-sm hover:bg-white/15"
            >
              <BookOpen size={16} /> Read each section
            </a>
          </div>
        </div>
      </section>

      {/* CHARITY SNAPSHOT */}
      {data?.charity && (
        <section className="max-w-5xl mx-auto px-4 sm:px-6 -mt-8 relative z-10">
          <div className="bg-white border border-navy/10 rounded-xl shadow-md p-6 sm:p-8" data-testid="governance-snapshot">
            <p className="text-[10px] uppercase tracking-[0.25em] text-saffron">Charity snapshot</p>
            <h2 className="font-display text-2xl text-navy mt-1">{data.charity.name}</h2>
            <div className="mt-4 grid sm:grid-cols-2 gap-3 text-sm">
              <Row k="Legal structure" v={data.charity.structure} />
              <Row k="Registered office" v={data.charity.registered_office} />
              <Row k="Website" v={data.charity.website} />
            </div>
            <div className="mt-5 pt-4 border-t border-navy/5">
              <p className="text-[10px] uppercase tracking-[0.25em] text-navy/55">Charitable objects</p>
              <p className="mt-1.5 text-sm text-navy/80 font-serif leading-relaxed">{data.charity.objects}</p>
            </div>
          </div>
        </section>
      )}

      {/* TRUSTEES */}
      {data?.trustees && (
        <section className="max-w-5xl mx-auto px-4 sm:px-6 mt-10" data-testid="governance-trustees">
          <p className="text-[10px] uppercase tracking-[0.25em] text-saffron">The trustees</p>
          <h2 className="font-display text-3xl text-navy mt-1">Founder trustees</h2>
          <p className="mt-2 text-navy/65 max-w-2xl text-sm">
            ABGN-UK is led by a volunteer trustee board. All trustees serve without
            remuneration. Joint and several responsibility applies to every decision.
          </p>
          <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
            {data.trustees.map((t, i) => (
              <motion.div
                key={t.name}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                data-testid={`governance-trustee-${i}`}
                className="bg-white border border-navy/10 rounded-xl p-5"
              >
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 rounded-full bg-saffron/15 text-saffron font-display text-xl flex items-center justify-center flex-shrink-0">
                    {t.name.replace(/^(Mr|Miss|Mrs|Ms|Dr)\s+/, "").charAt(0)}
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-display text-lg text-navy leading-tight">{t.name}</h3>
                    <p className="text-[10px] uppercase tracking-widest text-saffron mt-0.5">{t.role}</p>
                    <p className="text-sm text-navy/70 mt-2 leading-relaxed">{t.responsibilities}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </section>
      )}

      {/* SECTIONS — the actual book chapters */}
      <section id="sections" className="max-w-5xl mx-auto px-4 sm:px-6 mt-12 pb-16">
        <p className="text-[10px] uppercase tracking-[0.25em] text-saffron">Chapters</p>
        <h2 className="font-display text-3xl text-navy mt-1">The 11 sections</h2>
        <p className="mt-2 text-navy/65 max-w-2xl text-sm">
          Each chapter is a stand-alone policy that the trustees review annually
          and update as the charity grows.
        </p>

        {loading ? (
          <div className="mt-8 p-12 text-center text-navy/55">Loading…</div>
        ) : (
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-3">
            {(data?.sections || []).map((sec) => {
              const Icon = SECTION_ICONS[sec.id] || FileText;
              const inner = (
                <div className="bg-white border border-navy/10 rounded-xl p-5 hover:shadow-md hover:border-saffron/40 transition-all h-full">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-md bg-navy/5 text-navy flex items-center justify-center flex-shrink-0">
                      <Icon size={16} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] font-mono text-navy/40">CHAPTER {String(sec.n).padStart(2, "0")}</span>
                        {sec.url && (
                          <ExternalLink size={10} className="text-navy/30" />
                        )}
                      </div>
                      <h3 className="font-display text-base text-navy mt-0.5 leading-tight">{sec.title}</h3>
                      <p className="text-xs text-navy/60 mt-1.5 leading-relaxed">{sec.summary}</p>
                      {sec.url ? (
                        <span className="inline-flex items-center gap-1 mt-2.5 text-xs text-saffron font-medium">
                          Read in full <ChevronRight size={11} />
                        </span>
                      ) : (
                        <span className="inline-block mt-2.5 text-xs text-navy/45 italic">
                          In the downloadable PDF only
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
              return sec.url ? (
                <Link key={sec.id} to={sec.url} data-testid={`governance-section-${sec.id}`}>{inner}</Link>
              ) : (
                <div key={sec.id} data-testid={`governance-section-${sec.id}`}>{inner}</div>
              );
            })}
          </div>
        )}

        {/* Closing CTA */}
        <div className="mt-12 bg-navy text-pearl rounded-xl p-6 sm:p-8 text-center">
          <h3 className="font-display text-2xl">A living document</h3>
          <p className="mt-2 max-w-2xl mx-auto text-pearl/75 text-sm">
            This pack is reviewed at every trustee meeting and updated whenever
            policy or practice changes. The PDF below is generated live from
            our most recent text — there is no stale archive copy.
          </p>
          <a
            href={pdfUrl}
            data-testid="governance-download-pack-bottom"
            className="mt-5 inline-flex items-center gap-2 px-6 py-3 bg-saffron text-navy rounded-md text-sm font-medium hover:bg-saffron-light"
          >
            <Download size={16} /> Download the Governance Pack
          </a>
        </div>
      </section>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[10px] uppercase tracking-[0.2em] text-navy/50">{k}</span>
      <span className="text-navy/85">{v}</span>
    </div>
  );
}
